import { Link } from 'react-router-dom';
import { ShoppingCart, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCart } from '@/contexts/CartContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useProductMainImage } from '@/hooks/useProductMainImage';
import { buildProductUrl } from '@/utils/seo';
import type { Product } from '@/types/product';

interface ProductCardProps {
  product: Product;
}

export const ProductCard = ({ product }: ProductCardProps) => {
  const { addToCart } = useCart();
  const { t, language } = useLanguage();
  const isOutOfStock = product.stock_quantity === 0;
  const productImage = useProductMainImage(product.id, product.image);

  const getName = () => {
    return product.name[language as 'sv' | 'en' | 'ar'];
  };

  const getShortDescription = () => {
    const desc = product.shortDescription || product.description;
    if (!desc) return '';
    return desc[language as 'sv' | 'en' | 'ar'];
  };

  const isDiscountActive = () => {
    if (!product.on_discount || !product.discount_percentage) return false;
    const now = new Date();
    if (product.discount_start_date && new Date(product.discount_start_date) > now) return false;
    if (product.discount_end_date && new Date(product.discount_end_date) < now) return false;
    return true;
  };

  const getDiscountedPrice = () => {
    if (!isDiscountActive()) return product.price;
    return product.price * (1 - (product.discount_percentage! / 100));
  };

  const getDiscountLabel = () => {
    const labels = {
      sv: 'Rea',
      en: 'Sale',
      ar: 'تخفيض'
    };
    return labels[language as keyof typeof labels];
  };

  // Build SEO-friendly product URL
  const productUrl = buildProductUrl(product);

  return (
    <div className="product-card group animate-fade-in flex flex-col">
      <Link to={productUrl} className="flex-1">
        <div className="relative overflow-hidden aspect-square">
          {isOutOfStock && (
            <div className="absolute inset-0 bg-black/70 z-10 flex items-center justify-center backdrop-blur-[2px]">
              <div className="text-center text-white px-4">
                <p className="text-lg font-bold mb-1">
                  {language === 'sv' ? 'Slut i lager' : language === 'ar' ? 'نفذ من المخزون' : 'Out of Stock'}
                </p>
                <p className="text-xs opacity-90">
                  {language === 'sv' ? 'Tillbaka i lager om 2 veckor' : language === 'ar' ? 'العودة في المخزون خلال أسبوعين' : 'Back in stock in 2 weeks'}
                </p>
              </div>
            </div>
          )}
          <img
            src={productImage}
            alt={`${getName()} - ${language === 'sv' ? 'Ekologisk hudvårdsprodukt' : language === 'ar' ? 'منتج عناية بالبشرة عضوي' : 'Organic skincare product'} from Càivonclub`}
            className="w-full h-full object-cover group-hover:scale-105 smooth-transition"
            width="400"
            height="400"
            loading="lazy"
            decoding="async"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          />
          {isDiscountActive() && (
            <Badge className="absolute top-2 left-2 bg-destructive text-destructive-foreground flex items-center gap-1">
              <Tag className="h-3 w-3" />
              -{product.discount_percentage}%
            </Badge>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 smooth-transition" />
        </div>

        <div className="p-4 space-y-3">
          <h3 className="font-serif text-lg font-medium text-foreground group-hover:text-primary smooth-transition">
            {getName()}
          </h3>

          <p className="text-sm text-muted-foreground line-clamp-2">
            {getShortDescription()}
          </p>

          <div className="flex flex-col gap-1">
            {isDiscountActive() ? (
              <>
                <span className="text-lg font-semibold text-primary">
                  {getDiscountedPrice().toFixed(2)} SEK
                </span>
                <span className="text-sm text-muted-foreground line-through">
                  {product.price} SEK
                </span>
              </>
            ) : (
              <span className="text-lg font-semibold text-primary">
                {product.price} SEK
              </span>
            )}
          </div>
        </div>
      </Link>

      {/* Add to Cart button outside the link, at bottom of card */}
      <div className="px-4 pb-4">
        <Button
          size="sm"
          className="w-full btn-secondary"
          onClick={async () =>
            await addToCart({
              id: product.id,
              name: getName(),
              price: isDiscountActive() ? getDiscountedPrice() : product.price,
              image: productImage,
              stock_quantity: product.stock_quantity,
            })
          }
          disabled={isOutOfStock}
        >
          <ShoppingCart className="h-4 w-4 mr-2" />
          {t('addToCart')}
        </Button>
      </div>
    </div>
  );
};